import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Activity, BrainCircuit, CheckCircle2, Clock, Coins, FileText, Loader2, Search, XCircle, Zap, ChevronLeft, ChevronRight } from "lucide-react";

function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("zh-HK", {
    timeZone: "Asia/Hong_Kong",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  });
}

function getModeLabel(mode: number) {
  if (mode === 0) return "文件預檢";
  if (mode === 1) return "模式 1: 空白標書";
  if (mode === 2) return "模式 2: 已填標書";
  if (mode === 3) return "模式 3: 公司提案";
  if (mode === 4) return "社交文案";
  return `模式 ${mode}`;
}

function StatCard({ title, value, subtitle, icon: Icon }: { title: string, value: string | number, subtitle: string, icon: any }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-start gap-4">
        <div className="p-2.5 bg-primary/10 text-primary rounded-lg shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">{title}</p>
          <p className="text-xl font-bold truncate">{value}</p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminAiRecordPage() {
  const { getToken } = useAuth();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  const [llm, setLlm] = useState("all");
  const [mode, setMode] = useState("all");
  const [success, setSuccess] = useState("all");
  const [hasTender, setHasTender] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page on filter changes (excluding page itself)
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, llm, mode, success, hasTender, from, to]);

  const { data, isLoading, isError, error, isPlaceholderData } = useQuery({
    queryKey: ["admin-ai-record", debouncedSearch, llm, mode, success, hasTender, from, to, page, limit],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (llm !== "all") params.set("llm", llm);
      if (mode !== "all") params.set("mode", mode);
      if (success !== "all") params.set("success", success);
      if (hasTender !== "all") params.set("hasTender", hasTender);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      params.set("page", page.toString());
      params.set("limit", limit.toString());

      const res = await fetch(`/api/admin/ai-record?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error ?? "讀取失敗");
      return res.json();
    },
    placeholderData: (prev) => prev,
  });

  const totals = data?.totals;
  const records = data?.records ?? [];
  const pagination = data?.pagination;

  return (
    <AdminLayout tab="ai-record">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">AI 使用紀錄</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            檢視與分析全站會員的 AI 標書助手使用歷史、Tokens 消耗與成本。
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            title="使用次數" 
            value={totals?.recordCount ?? 0} 
            icon={Activity} 
            subtitle={`成功 ${totals?.successCount ?? 0} / 失敗 ${totals?.failureCount ?? 0}`} 
          />
          <StatCard 
            title="消耗 Tokens" 
            value={(totals?.totalTokens ?? 0).toLocaleString()} 
            icon={BrainCircuit} 
            subtitle={`P: ${(totals?.promptTokens ?? 0).toLocaleString()} / C: ${(totals?.completionTokens ?? 0).toLocaleString()}`} 
          />
          <StatCard 
            title="消耗 SCoins" 
            value={(totals?.scoins ?? 0).toFixed(2)} 
            icon={Coins} 
            subtitle={`約 $${(totals?.costUsd ?? 0).toFixed(4)} USD`} 
          />
          <StatCard 
            title="平均耗時" 
            value={totals?.avgDurationMs ? (totals.avgDurationMs / 1000).toFixed(1) + "s" : "0.0s"} 
            icon={Clock} 
            subtitle={`總計 ${(totals?.totalDurationMs ? totals.totalDurationMs / 1000 / 60 : 0).toFixed(1)} min`} 
          />
        </div>

        {/* Filters */}
        <div className="p-4 border rounded-xl bg-card flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="搜尋會員、信箱、標題、模型..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              className="w-64 h-9 pl-8" 
            />
          </div>

          <div className="flex items-center gap-2">
            <Input 
              type="date" 
              value={from} 
              onChange={e => setFrom(e.target.value)} 
              className="w-36 h-9 text-sm" 
            />
            <span className="text-muted-foreground text-sm">-</span>
            <Input 
              type="date" 
              value={to} 
              onChange={e => setTo(e.target.value)} 
              className="w-36 h-9 text-sm" 
            />
          </div>

          <Select value={llm} onValueChange={setLlm}>
            <SelectTrigger className="w-32 h-9 text-sm"><SelectValue placeholder="AI 模型" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">所有模型</SelectItem>
              <SelectItem value="gemini">Gemini</SelectItem>
              <SelectItem value="claude">Claude</SelectItem>
              <SelectItem value="chatgpt">ChatGPT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-32 h-9 text-sm"><SelectValue placeholder="功能模式" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">所有模式</SelectItem>
              <SelectItem value="0">文件預檢</SelectItem>
              <SelectItem value="1">模式 1 (空白)</SelectItem>
              <SelectItem value="2">模式 2 (已填)</SelectItem>
              <SelectItem value="3">模式 3 (提案)</SelectItem>
              <SelectItem value="4">社交文案</SelectItem>
            </SelectContent>
          </Select>

          <Select value={success} onValueChange={setSuccess}>
            <SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="狀態" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">所有狀態</SelectItem>
              <SelectItem value="true">成功</SelectItem>
              <SelectItem value="false">失敗</SelectItem>
            </SelectContent>
          </Select>

          <Select value={hasTender} onValueChange={setHasTender}>
            <SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="關聯標書" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">不限</SelectItem>
              <SelectItem value="true">有標書</SelectItem>
              <SelectItem value="false">無標書</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { 
              setSearch(""); setLlm("all"); setMode("all"); setSuccess("all"); 
              setHasTender("all"); setFrom(""); setTo(""); setPage(1); 
            }} 
            className="h-9 ml-auto md:ml-0"
          >
            重設
          </Button>
        </div>

        {/* Table */}
        <div className="border rounded-xl bg-card overflow-hidden relative">
          {isPlaceholderData && (
            <div className="absolute inset-0 z-10 bg-background/50 backdrop-blur-[1px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          )}
          
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-16">ID</TableHead>
                <TableHead className="w-36">時間</TableHead>
                <TableHead className="w-48">會員</TableHead>
                <TableHead className="max-w-[200px]">功能與關聯標書</TableHead>
                <TableHead className="w-20 text-center">輸入源</TableHead>
                <TableHead className="w-36">模型與耗時</TableHead>
                <TableHead className="w-36 text-right">Tokens</TableHead>
                <TableHead className="w-24 text-right">花費</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && !data ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={8} className="py-4"><Skeleton className="h-8 w-full" /></TableCell>
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center text-destructive">
                    載入失敗：{(error as Error).message}
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                    找不到符合條件的紀錄
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record: any) => (
                  <TableRow key={record.id} className="group hover:bg-muted/30 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {record.success ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help"><CheckCircle2 className="h-4 w-4 text-green-500" /></span>
                            </TooltipTrigger>
                            <TooltipContent>成功</TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help"><XCircle className="h-4 w-4 text-destructive" /></span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">{record.errorMsg || "失敗"}</TooltipContent>
                          </Tooltip>
                        )}
                        <span className="text-xs font-mono text-muted-foreground">#{record.id}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80">{formatDateTime(record.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate" title={record.memberName || record.userId}>
                          {record.memberName || record.userId || "未知會員"}
                        </span>
                        {record.memberEmail && (
                          <span className="text-xs text-muted-foreground truncate" title={record.memberEmail}>
                            {record.memberEmail}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5 items-start">
                        <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm font-medium border-border/50 shadow-sm">
                          {getModeLabel(record.mode)}
                        </Badge>
                        {record.tenderId ? (
                          <div className="text-xs truncate w-full flex items-center" title={record.tenderTitle || ""}>
                            <span className="font-mono text-muted-foreground/80 mr-1 shrink-0">#{record.tenderId}</span>
                            <span className="truncate">{record.tenderTitle}</span>
                          </div>
                        ) : <span className="text-xs text-muted-foreground/50">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        {record.hasPdf ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help"><FileText className="h-4 w-4 text-blue-500/90" /></span>
                            </TooltipTrigger>
                            <TooltipContent>包含文件附件</TooltipContent>
                          </Tooltip>
                        ) : <span className="w-4" />}
                        
                        {record.hasExtraText ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help"><Zap className="h-4 w-4 text-amber-500/90" /></span>
                            </TooltipTrigger>
                            <TooltipContent>包含文字補充</TooltipContent>
                          </Tooltip>
                        ) : <span className="w-4" />}
                        
                        {!record.hasPdf && !record.hasExtraText && (
                          <span className="absolute text-xs text-muted-foreground/40">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-xs font-medium truncate" title={record.modelId}>{record.modelId}</span>
                        <span className="text-[10px] text-muted-foreground mt-0.5">
                          {record.durationMs ? (record.durationMs / 1000).toFixed(1) + "s" : "—"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col">
                        <span className="text-xs font-medium font-mono">{record.totalTokens?.toLocaleString() || "—"}</span>
                        <span className="text-[10px] text-muted-foreground font-mono mt-0.5">
                          P: {record.promptTokens?.toLocaleString() || "0"} / C: {record.completionTokens?.toLocaleString() || "0"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-primary">{record.scoins ? record.scoins.toFixed(2) : "—"} S</span>
                        <span className="text-[10px] text-muted-foreground font-mono mt-0.5">${record.costUsd ? record.costUsd.toFixed(4) : "0"}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-muted/20 flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">
                共 {pagination.totalCount} 筆，頁數 {pagination.page} / {pagination.totalPages}
              </span>
              <div className="flex gap-1.5">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={pagination.page <= 1} 
                  onClick={() => setPage(p => p - 1)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={pagination.page >= pagination.totalPages} 
                  onClick={() => setPage(p => p + 1)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
